import React, { ReactElement } from 'react';
import styled from 'styled-components';
import SwaggerUI from 'swagger-ui-react';
import 'swagger-ui-react/swagger-ui.css';
import { Config } from './types';
import { isSafeSwaggerUrl } from './uploadwidget.util';

const StyledUploadWidget = styled.div``;

/*
 * StyledSwaggerContainer wraps the swagger-ui-react output. It exists to:
 *  - constrain the spec to its widget box (height/max-height: 100%, min-height: 0)
 *    so a long API spec scrolls internally (overflow-y: auto) instead of pushing
 *    the page layout, while overflow-x is hidden to avoid horizontal scrollbars.
 *  - force `pointer-events: auto` so the Swagger UI stays clickable even when the
 *    widget is dropped beneath a higher-stacked widget (e.g. a Navigation widget);
 *    the PageLayout z-index rules raise the widget, this keeps it interactive.
 *  - hide the default Swagger top bar and the spec URL shown in the info header,
 *    which would otherwise leak the (signed) source URL to end users.
 */
const StyledSwaggerContainer = styled.div`
  height: 100%;
  max-height: 100%;
  min-height: 0;
  width: 100%;
  overflow-x: hidden;
  overflow-y: auto;
  pointer-events: auto;

  /** Hide the top bar that displays the uploaded spec URL */
  .swagger-ui .topbar {
    display: none;
  }

  /** Hide the spec URL shown below the API title in the info section */
  .swagger-ui .info .base-url,
  .swagger-ui .info hgroup a {
    display: none;
  }
`;

/**
 * UploadWidget renders content based on the upload mode stored in the widget config.
 * - In "horizon" mode the widget is a placeholder that gets replaced after upload.
 * - In "swagger" mode the widget renders interactive OpenAPI documentation via swagger-ui-react.
 *
 * @param props - Standard widget render props containing the widget config.
 * @returns The rendered widget element.
 */
const UploadWidget: React.FC<WidgetRenderProps<Config>> = (
  props: WidgetRenderProps<Config>
): ReactElement => {
  const {
    visible,
    isCustomWidget,
    uploadMode,
    uploadedFile,
  } = props.widgetState.config;

  if (visible === false || isCustomWidget) {
    return <></>;
  }

  /**
   * When in swagger mode, only render the Swagger UI for a file that has a safe,
   * absolute http(s) URL. Validating the URL before passing it to swagger-ui-react
   * prevents an attacker-controlled value (e.g. `javascript:`/`data:`) from being
   * fetched or rendered, mitigating XSS/injection risk.
   */
  if (uploadMode === 'swagger' && isSafeSwaggerUrl(uploadedFile?.url)) {
    return (
      <StyledSwaggerContainer data-test-id="upload-widget-swagger-ui">
        <SwaggerUI url={uploadedFile?.url} />
      </StyledSwaggerContainer>
    );
  }

  return (
    <StyledUploadWidget
      style={{
        marginTop: '0',
        height: '100%',
        width: '100%',
      }}
    ></StyledUploadWidget>
  );
};
export default UploadWidget;
